import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type { PrintablesLibrarySetEntry } from 'teacher-ui-client/contentful-client/entry-types'
import { PrintablesBookRole } from 'teacher-ui-client/components/printables/roles'
import ResourceCardBookDetails from 'teacher-ui-client/components/printables/resource-card/book-details'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import { concat, fn } from '@ember/helper'
import { eq } from 'ember-truth-helpers'
import fullGradeName from 'teacher-ui-client/helpers/full-grade-name'
import RadioButtonGroup from 'teacher-ui-client/components/base/radio-button-group'
import LayoutBlocksModal from 'teacher-ui-client/components/layout-blocks/modal'
import { BookResourceOptions } from 'teacher-ui-client/components/printables/resource-card/book-resource-options'
import { assert } from '@ember/debug'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'

interface Signature {
  Element: HTMLDivElement
  Args: {
    subscriptionType?: SubscriptionType
    bookSet: PrintablesLibrarySetEntry
    role: PrintablesBookRole
  }
}

const { Student, Teacher } = PrintablesBookRole

export default class ResourceCardBook extends Component<Signature> {
  @service intl!: IntlService

  @tracked isShowingBookModal = false

  @tracked
  activeRoleInModal: PrintablesBookRole = Student

  get activeBook() {
    const activeBookFromSet = this.args.bookSet.fields.books?.find((book) => {
      return book?.fields.role === this.args.role
    })

    return activeBookFromSet?.fields
  }

  get activeBookInModal() {
    const activeBookFromSet = this.args.bookSet.fields.books?.find((book) => {
      return book?.fields.role === this.activeRoleInModal
    })

    const activeBookFields = activeBookFromSet?.fields ?? this.args.bookSet.fields.books[0]?.fields
    assert('activeBookFields missing', activeBookFields)
    return activeBookFields
  }

  get bookSetHasBothRoles() {
    const activeBookFromSet = this.args.bookSet.fields.books?.map((book) => {
      return book?.fields.role
    })
    return activeBookFromSet.includes(Teacher) && activeBookFromSet.includes(Student)
  }

  get activeBookCover() {
    return this.activeBook?.bookCover?.fields.file?.url
  }

  get switchRoleButtons() {
    return [
      { value: Student, text: this.intl.t('printables.student') },
      { value: Teacher, text: this.intl.t('printables.teacher') },
    ]
  }

  // Set the modal's active role if the user opens the popup by clicking on a card again.
  // This prevents an issue where the modal remembers its state from the last time it was opened where it doesn't match what the user clicked to open it this time.
  @action
  showBookModal(role?: PrintablesBookRole): void {
    this.activateRole(role)
    this.isShowingBookModal = true
  }

  @action
  closeBookModal() {
    this.isShowingBookModal = false
  }

  @action
  activateRole(value: unknown) {
    const role = value as PrintablesBookRole
    if (role !== this.activeRoleInModal) {
      this.activeRoleInModal = role
    }
  }

  <template>
    <div class="flex-col rounded-b-md border-2 p-3" ...attributes>
      <img
        src="{{concat this.activeBookCover '?h=250'}}"
        class="border-dusty-black-100 mx-auto block w-1/2 cursor-pointer rounded-md border border-solid p-1 shadow-xs"
        alt={{t "printables.bookCoverPdf"}}
        {{on "click" (fn this.showBookModal this.activeBook.role)}}
        role="button"
      />
      <div class="mt-3 text-center">
        {{fullGradeName @bookSet.fields.gradePosition}}
      </div>
      <h5 class="mt-4 grow text-center">
        {{@bookSet.fields.bookTitle}}
      </h5>
      <div class="mt-6 text-center">
        {{#if (eq this.activeBook.role PrintablesBookRole.Teacher)}}
          {{t "printables.teacherBook"}}
        {{else}}
          {{t "printables.studentBook"}}
        {{/if}}
      </div>
      <div class="mt-4 text-center font-semibold">
        <button type="button" class="default-link-style" {{on "click" (fn this.showBookModal this.activeBook.role)}}>
          {{t "printables.moreInfo"}}
          <PhosphorIcon @icon="phosphor-arrow-circle-right-fill" />
        </button>
      </div>
      {{#if this.activeBook}}
        <BookResourceOptions class="mt-6" @activeBook={{this.activeBook}} @subscriptionType={{@subscriptionType}} />
      {{/if}}
    </div>
    <LayoutBlocksModal @showModal={{this.isShowingBookModal}} @close={{this.closeBookModal}}>
      {{#if this.bookSetHasBothRoles}}
        <RadioButtonGroup
          class="mx-auto my-4 grid w-1/2 grid-flow-col grid-rows-1"
          @name="role-select"
          @buttons={{this.switchRoleButtons}}
          @value={{this.activeBookInModal.role}}
          @onSelect={{this.activateRole}}
        />
      {{/if}}
      <ResourceCardBookDetails
        @bookSet={{@bookSet}}
        @activeBook={{this.activeBookInModal}}
        @subscriptionType={{@subscriptionType}}
      />
    </LayoutBlocksModal>
  </template>
}
